/**
 * AtAGlance Component
 *
 * Compact "at a glance" summary of a restaurant's identity: categories,
 * cuisine types, and a curated set of keyword highlights. Used in the
 * restaurant profile chip redesign to give users a quick understanding
 * of what makes a place special.
 *
 * Sections render conditionally -- categories, cuisine types, and highlights
 * are each shown only when data is available. If all three sections would
 * be empty, the component returns null.
 *
 * Highlights are selected by the `selectSmartHighlights` algorithm,
 * which picks a diverse cross-section of keywords from the most
 * interesting groups first (entertainment, views, beverages, etc.).
 */

import React, { useMemo } from 'react';
import {
  HStack,
  VStack,
  Text,
} from '@chakra-ui/react';
import { Sparkles } from 'lucide-react';
import type { Restaurant } from '@/types/restaurant';
import {
  useUIMetadata,
  getCategoryDisplayName,
} from '@/api/queries/useConfigQueries';
import { getIconComponent } from '@/utils/iconUtils';
import { selectSmartHighlights } from '@/utils/keywordScoring';
import { UnifiedChip } from './UnifiedChip';
import { ALLERGY_ACCOMMODATION_KEYS } from './allergyAccommodationOptions';

// =============================================================================
// Cuisine Accent Colors (derived from national flag palettes)
// =============================================================================

/** Maps cuisine keys to a prominent color from the country's flag. */
const CUISINE_ACCENT_COLORS: Record<string, string> = {
  '2AMERICAN': '#DC2626',
  '2ITALIAN': '#16A34A',
  '2JAPANESE': '#DC2626',
  '2MEXICAN': '#16A34A',
  '2CHINESE': '#DC2626',
  '2INDIAN': '#EA580C',
  '2FRENCH': '#2563EB',
  '2THAI': '#EA580C',
  '2KOREAN': '#2563EB',
  '2SPANISH': '#DC2626',
  '2GREEK': '#2563EB',
  '2VIETNAMESE': '#DC2626',
  '2BRAZILIAN': '#16A34A',
  '2ETHIOPIAN': '#16A34A',
  '2CARIBBEAN': '#0D9488',
  '2MEDITERRANEAN': '#2563EB',
  '2GERMAN': '#DC2626',
  '2BRITISH': '#DC2626',
  '2TURKISH': '#DC2626',
  '2LEBANESE': '#DC2626',
  '2PERUVIAN': '#DC2626',
  '2COLOMBIAN': '#FACC15',
  '2CUBAN': '#2563EB',
  '2JAMAICAN': '#16A34A',
  '2AFRICAN': '#16A34A',
  '2ASIAN': '#DC2626',
  '2MIDDLE_EASTERN': '#16A34A',
};

const DEFAULT_CUISINE_ACCENT = '#805AD5';

// =============================================================================
// Props
// =============================================================================

export interface AtAGlanceProps {
  /** Restaurant data including categories, cuisineTypes, and keywords */
  restaurant: Restaurant;
  /** Maximum number of keyword highlights to display (default: 6) */
  maxHighlights?: number;
  /**
   * Learn More tokens are still in flight (profile opened from list data).
   * Cuisine chips render inert until they arrive. See `UnifiedChipProps`.
   */
  learnMorePending?: boolean;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Renders a compact at-a-glance summary of a restaurant.
 *
 * Three optional rows:
 * 1. **Categories** - Business-defined categories (e.g., "Fine Dining", "Wine Bar")
 * 2. **Cuisine Types** - Cuisine flags and names (e.g., "Italian", "Japanese")
 * 3. **Highlights** - Curated keyword highlights with a section header
 *
 * @example
 * ```tsx
 * <AtAGlance restaurant={restaurant} maxHighlights={4} />
 * ```
 */
function AtAGlanceInner({
  restaurant,
  maxHighlights = 6,
  learnMorePending = false,
}: AtAGlanceProps) {
  const { data: uiMetadata } = useUIMetadata();

  // Resolve categories to display names
  const categories = restaurant.categories ?? [];

  // Select diverse keyword highlights via the scoring algorithm. Allergy
  // keywords are skipped because they have their own badge grid
  // (AllergyAccommodations, paire-app#128). As chips, "Shellfish Allergy"
  // would also read like a warning rather than an accommodation.
  const highlights = useMemo(
    () =>
      selectSmartHighlights(
        (restaurant.keywords ?? []).filter(kw => !ALLERGY_ACCOMMODATION_KEYS.has(kw)),
        uiMetadata,
        maxHighlights
      ),
    [restaurant.keywords, uiMetadata, maxHighlights]
  );

  const cuisineTypes = restaurant.cuisineTypes ?? [];

  // If all sections are empty, render nothing
  const hasCategories = categories.length > 0;
  const hasCuisines = cuisineTypes.length > 0;
  const hasHighlights = highlights.length > 0;

  if (!hasCategories && !hasCuisines && !hasHighlights) {
    return null;
  }

  return (
    <VStack
      as="section"
      aria-label="At a Glance"
      align="stretch"
      spacing={4}
    >
      {/* Categories row */}
      {hasCategories && (
        <HStack wrap="wrap" gap={2}>
          {categories.map(cat => {
            const displayName = getCategoryDisplayName(cat, uiMetadata);

            return (
              <UnifiedChip
                key={cat}
                variant="category"
                label={displayName}
                categoryKey={cat}
              />
            );
          })}
        </HStack>
      )}

      {/* Cuisine types row */}
      {hasCuisines && (
        <HStack wrap="wrap" gap={2}>
          {cuisineTypes.map(ct => (
            <UnifiedChip
              key={ct.key}
              variant="cuisine"
              label={ct.displayName}
              flagEmoji={ct.flagEmoji}
              accentColor={CUISINE_ACCENT_COLORS[ct.key] ?? DEFAULT_CUISINE_ACCENT}
              learnMoreUrl={ct.learnMoreUrl}
              learnMoreToken={ct.learnMoreToken}
              learnMorePending={learnMorePending}
            />
          ))}
        </HStack>
      )}

      {/* Highlights section with header */}
      {hasHighlights && (
        <VStack align="stretch" spacing={2}>
          <HStack spacing={1.5}>
            {/* SVG gradient definition for sparkles icon */}
            <svg width="0" height="0" style={{ position: 'absolute' }}>
              <defs>
                <linearGradient id="sparkles-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F472B6" /> {/* pink.400 */}
                  <stop offset="100%" stopColor="#FBBF24" /> {/* yellow.400 */}
                </linearGradient>
              </defs>
            </svg>

            {/* Sparkles icon with gradient fill */}
            <HStack
              as="span"
              spacing={0}
              sx={{
                '& svg': {
                  fill: 'url(#sparkles-gradient)',
                  stroke: 'url(#sparkles-gradient)',
                },
              }}
            >
              <Sparkles size={16} />
            </HStack>

            <Text
              fontSize="sm"
              fontWeight="semibold"
              color="var(--card-text)"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Highlights
            </Text>
          </HStack>
          <HStack wrap="wrap" gap={2}>
            {highlights.map(h => {
              const IconComponent = h.icon
                ? getIconComponent(h.icon)
                : undefined;

              return (
                <UnifiedChip
                  key={h.key}
                  variant="highlight"
                  label={h.displayName}
                  group={h.group}
                  icon={IconComponent}
                />
              );
            })}
          </HStack>
        </VStack>
      )}
    </VStack>
  );
}

export const AtAGlance = React.memo(AtAGlanceInner);
AtAGlance.displayName = 'AtAGlance';
